import React, { useState } from 'react';
import api from '../../config/axios';

const apiPath = path => {
  const baseUrl = String(api.defaults.baseURL || '').replace(/\/$/, '');
  return `${baseUrl.endsWith('/api') ? '' : '/api'}${path}`;
};

const errorMessage = async (error, fallback) => {
  if (error.response?.data instanceof Blob) {
    try {
      const payload = JSON.parse(await error.response.data.text());
      return payload.message || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return error.response?.data?.message || error.message || fallback;
};

export default function PropertyManagerReportActions({ propertyId, propertyName, summary }) {
  const [downloading, setDownloading] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showRecipientDialog, setShowRecipientDialog] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const downloadPdf = async () => {
    setError('');
    setSuccess('');
    setDownloading(true);
    try {
      const response = await api.get(
        apiPath(`/property-performance/${propertyId}/property-manager-report/pdf`),
        { responseType: 'blob' },
      );
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fallback = `${propertyName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-property-manager-briefing.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filenameMatch?.[1] || fallback;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(await errorMessage(downloadError, 'Unable to download the property-manager report'));
    } finally {
      setDownloading(false);
    }
  };

  const openRecipientDialog = async () => {
    setError('');
    setSuccess('');
    setLoadingRecipients(true);
    try {
      const response = await api.get(
        apiPath(`/property-performance/${propertyId}/property-manager-report/recipients`),
      );
      const nextRecipients = response.data?.recipients || [];
      setRecipients(nextRecipients);
      setSelectedIds(nextRecipients.filter(recipient => recipient.email).map(recipient => recipient.userId));
      setShowRecipientDialog(true);
    } catch (recipientError) {
      setError(await errorMessage(recipientError, 'Unable to load the assigned property managers'));
    } finally {
      setLoadingRecipients(false);
    }
  };

  const toggleRecipient = userId => {
    setSelectedIds(current => current.includes(userId)
      ? current.filter(id => id !== userId)
      : [...current, userId]);
  };

  const sendReport = async () => {
    if (!selectedIds.length) {
      setError('Select at least one property manager.');
      return;
    }
    setError('');
    setSending(true);
    try {
      const response = await api.post(
        apiPath(`/property-performance/${propertyId}/property-manager-report/email`),
        { recipientUserIds: selectedIds },
      );
      const sentCount = Number(response.data?.sentCount || selectedIds.length);
      setSuccess(`Report emailed to ${sentCount} property manager${sentCount === 1 ? '' : 's'}.`);
      setShowRecipientDialog(false);
    } catch (sendError) {
      setError(await errorMessage(sendError, 'Unable to email the property-manager report'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={downloadPdf}
          disabled={!summary || downloading}
          className="whitespace-nowrap rounded-md border border-sky-300 bg-white px-4 py-2 text-sm font-medium text-sky-800 hover:bg-sky-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
        >
          {downloading ? 'Preparing PDF…' : 'Download PDF'}
        </button>
        <button
          type="button"
          onClick={openRecipientDialog}
          disabled={!summary || !summary.isCurrent || loadingRecipients}
          title={summary && !summary.isCurrent ? 'Regenerate the briefing before emailing it' : undefined}
          className="whitespace-nowrap rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {loadingRecipients ? 'Loading managers…' : 'Email managers'}
        </button>
      </div>

      {success && <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{success}</div>}
      {error && !showRecipientDialog && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {showRecipientDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="manager-report-recipient-title">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 id="manager-report-recipient-title" className="text-lg font-semibold text-gray-900">Email property-manager report</h3>
                <p className="mt-1 text-sm text-gray-600">Confirm the assigned managers who should receive the internal PDF for {propertyName}.</p>
              </div>
              <button type="button" onClick={() => setShowRecipientDialog(false)} disabled={sending} aria-label="Close" className="rounded p-1 text-xl text-gray-400 hover:bg-gray-100 hover:text-gray-700">×</button>
            </div>

            <div className="mt-5 space-y-2">
              {recipients.map(recipient => (
                <label key={recipient.userId} className={`flex items-start gap-3 rounded-lg border p-3 ${recipient.email ? 'cursor-pointer border-gray-200' : 'border-amber-200 bg-amber-50'}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(recipient.userId)}
                    onChange={() => toggleRecipient(recipient.userId)}
                    disabled={!recipient.email || sending}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{recipient.name}</span>
                    <span className={`block text-xs ${recipient.email ? 'text-gray-500' : 'text-amber-800'}`}>{recipient.email || 'No email address on file'}</span>
                  </span>
                </label>
              ))}
              {!recipients.length && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">No active property managers are assigned to this property.</div>
              )}
            </div>

            {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setShowRecipientDialog(false)} disabled={sending} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:text-gray-400">Cancel</button>
              <button type="button" onClick={sendReport} disabled={sending || !selectedIds.length} className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-gray-300">
                {sending ? 'Sending report…' : `Send to ${selectedIds.length || 0} manager${selectedIds.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
